/**
 * AuthPage Component
 * Page container for login, register, and 2FA forms
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Paper, Tabs, Tab, Container } from '@mui/material';
import LoginForm from '@/features/auth/LoginForm';
import RegisterForm from '@/features/auth/RegisterForm';
import TwoFactorInput from '@/features/auth/TwoFactorInput';
import useAuthStore from '@/store/authStore';

type AuthTab = 'login' | 'register';

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useAuthStore();
  const [activeTab, setActiveTab] = useState<AuthTab>('login');

  // Redirect if already authenticated
  useEffect(() => {
    if (status === 'authenticated') {
      const from = (location.state as { from?: Location })?.from?.pathname || '/frontend-design';
      navigate(from, { replace: true });
    }
  }, [status, navigate, location]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: AuthTab) => {
    setActiveTab(newValue);
  };

  const handleAuthSuccess = () => {
    const from = (location.state as { from?: Location })?.from?.pathname || '/frontend-design';
    navigate(from, { replace: true });
  };

  const handleCancelTwoFactor = () => {
    // Reset auth state and show login form
    useAuthStore.getState().logout();
    setActiveTab('login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #0b1220 0%, #1a1f3a 100%)'
            : 'linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
          }}
        >
          {status === 'requires2fa' ? (
            <TwoFactorInput onSuccess={handleAuthSuccess} onCancel={handleCancelTwoFactor} />
          ) : (
            <>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Iniciar Sesión" value="login" />
                <Tab label="Registrarse" value="register" />
              </Tabs>

              {activeTab === 'login' ? (
                <LoginForm
                  onSwitchToRegister={() => setActiveTab('register')}
                  onSuccess={handleAuthSuccess}
                />
              ) : (
                <RegisterForm
                  onSwitchToLogin={() => setActiveTab('login')}
                  onSuccess={handleAuthSuccess}
                />
              )}
            </>
          )}
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
          <Box component="span" sx={{ fontSize: '0.875rem' }}>
            Remote Assist © {new Date().getFullYear()}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
